import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ButtonComponent from '../components/Button';
import emailjs from '@emailjs/browser';
import trainers from '../data/trainers';
import SEO from '../components/SEO';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [inquiryErrors, setInquiryErrors] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock course data - in production, fetch from API
  const courses = {
    1: {
      id: 1,
      title: 'Basic Module',
      description: 'Comprehensive foundation course covering medical safety, clinical practices, drug development, and regulatory affairs fundamentals.',
      fullDescription: 'This comprehensive foundation course provides essential knowledge in medical safety, clinical practices, and regulatory affairs. Designed for beginners, it covers the fundamentals of clinical research, drug development lifecycle, epidemiological methods, and quality compliance. Perfect for professionals starting their career in pharmacovigilance and clinical research.',
      duration: '10 business days',
      level: 'Beginner',
      price: '₹25,000',
      features: [
        'Introduction to Medical Safety',
        'Good Clinical Practices',
        'Fundamentals of Clinical research',
        'Drug Development life cycle',
        'Epidemological methods',
        'Clinical Data Management and Clinical Information Flow',
        'Clinical data management reports',
        'Guidelines to Pharmacovigilance- Module I',
        'Introduction to regulatory affair',
        'Quality and Complaince'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    2: {
      id: 2,
      title: 'Advanced Module',
      description: 'Advanced training covering all basic topics plus public health ethics, biostatistics, regulations, and regulatory submissions.',
      fullDescription: 'Building upon the Basic Module, this advanced course covers all fundamental topics plus advanced concepts including public health ethics and policy, data management and biostatistics, regulations in clinical research, fundamentals of drug safety, lab data and AE management, medical writing, and regulatory submissions. Ideal for professionals looking to advance their knowledge and skills.',
      duration: '15 business days',
      level: 'Intermediate',
      price: '₹35,000',
      features: [
        'All basic topics plus',
        'Public health ethics and Policy',
        'Data Management and Biostatistics',
        'Regulations in clinical research',
        'Fundamentals of drug Safety',
        'Lab data and AE Management',
        'Importance of Medical writing in clinical research',
        'Guidelines to Pharmacovigilance- Module 2',
        'Regulatory Submissions',
        'Manufacturing and Supply chain priniciples'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    3: {
      id: 3,
      title: 'Pharmacovigilance',
      description: 'Comprehensive pharmacovigilance training covering post-marketing surveillance, regulatory reporting, MedDRA, and adverse event processing.',
      fullDescription: 'This comprehensive pharmacovigilance course provides in-depth training on all aspects of drug safety monitoring. Covering Good Clinical Practice, post-marketing surveillance, regulatory reporting workshops, handling safety data for marketed products, MedDRA introduction, regulatory inspections, narrative writing, adverse event report processing, global expectedness assessment, pregnancy reports, seriousness criteria, product complaints, and literature surveillance. Designed for professionals seeking expertise in pharmacovigilance operations.',
      duration: '45 business days',
      level: 'Advanced',
      price: '₹50,000',
      features: [
        'Pharmacovigilance: Good Clinical Practice',
        'Pharmacovigilance: Overview of Post marketing Surveillance',
        'Pharmacovigilance: Regulatory Reporting Workshop',
        'Pharmacovigilance: Handling Safety Data for Marketed Products',
        'MedDRA: An Introduction',
        'Handling regulatory Inspections',
        'Narrative Workshop',
        'Adverse Event Report Processing Guideline',
        'Guidelines for Global Expectedness Assessment',
        'Handling Pregnancy Reports',
        'Seriousness Criteria: Important Medical Event',
        'Handling Product Complaints with Adverse Event Reports',
        'Detecting and Reporting Adverse Events from Literature & Case'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    4: {
      id: 4,
      title: 'Advanced Pharmacovigilance',
      description: 'Customised training based on the Thrana Safety database covering case processing, case search, case inbox, and case entry.',
      fullDescription: 'This specialized course provides hands-on training using the Thrana Safety database system. Learn practical skills in case processing, case search functionality, managing case inbox, and case entry procedures. This customized training is designed for professionals who will be working directly with the Thrana Safety database platform.',
      duration: '15 days',
      level: 'Advanced',
      price: '₹35,000',
      features: [
        'Customised training based on the Thrana Safety database',
        'Case Processing in databases',
        'Case Search',
        'Case Inbox',
        'Case Entry'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    5: {
      id: 5,
      title: 'Clinical Data Management',
      description: 'Complete training on clinical data management including protocol development, SOPs, data validation, query management, and database lock.',
      fullDescription: 'Comprehensive training program covering all aspects of clinical data management from protocol development to database lock. Learn about SOP development, clinical trials, good clinical data management practices (GCDMP), data management plans, clinical data management systems, query management, data clarification forms, patient diaries, clinical data entry, data validation, SAE reconciliation, e-CRF design, report generation, database lock procedures, and clinical data archiving processes.',
      duration: '45 business days',
      level: 'Intermediate',
      price: '₹55,000',
      features: [
        'Introduction to Clinical data management',
        'Introduction to Clinical protocol',
        'SOP development',
        'Introduction to Clinical Trials',
        'Good clinical data management practices (GCDMP)',
        'Data Management Plan',
        'Clinical data management systems',
        'Clinical data repositories',
        'Query Management',
        'Data Clarification form',
        'Patient Dairy',
        'Clinical Data Entry',
        'Data validation',
        'SAE reconcillation',
        'e-CRF desining and data tracking from CRF',
        'Handing for different generated reports',
        'Database lock',
        'Clinical data Archiing process'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    6: {
      id: 6,
      title: 'Regulatory Affairs',
      description: 'Comprehensive regulatory affairs training covering drug development lifecycle, IND/NDA applications, ICH guidelines, and global regulations.',
      fullDescription: 'Extensive regulatory affairs training program covering the complete drug development lifecycle, regulatory frameworks, IND and NDA applications, ICH guidelines, pharmaceutical quality/CMC, post-approval regulatory affairs, labeling provisions, and regulations across multiple regions including India, FDA (US), Japan, Australia, Canada, and EMEA. Also covers medical devices, biosimilars, and Indian-specific regulations for clinical trials and pharmacovigilance.',
      duration: '45 business days',
      level: 'Advanced',
      price: '₹60,000',
      features: [
        'Drug development life cycle',
        'Understanding to drug regulations',
        'Historical perspective of drug regulations',
        'Good clinical Practices',
        'Regulatory affairs perspective in drug regulation',
        'Good manufacturing practices',
        'Drug approvals',
        'Investigational new drug (IND) application',
        'New Drug application (NDA)',
        'ICH history and evolution',
        'Common Technical document ICH',
        'Pharmaceutical quality /CMC',
        'INDs for Phase 2 and Phase 3 Studies',
        'Drug Master file',
        'Post Approval Regulatory affairs',
        'Post marketing adverse drug experience reporting experience',
        'Post approval maintenance of regulatory application/files',
        'Post approval changes',
        'General Labeling provisions',
        'Labeling: Prescription drugs',
        'Labeling: Over the counter products',
        'Drugs and Cosmetic Act',
        'Schedule Y',
        'Indian Regulatory affairs',
        'FDA regulatory environment and Guidance',
        'Regulations in Japan',
        'Regulations in Australia',
        'Regulations in Canada',
        'Regulatory process for Medical devices',
        'FDA- Biosimilars Guidance and Regulations',
        'EMEA- Biosimilars Guidance and Regulations',
        'Indian regulations-clinical trial and cosmetic biologicals',
        'Indian regulations-Pharmacovigilance requirements'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
    7: {
      id: 7,
      title: 'Soft Skills',
      description: 'Customized soft skills training for beginners and corporates, tailored to meet specific organizational requirements.',
      fullDescription: 'Customized soft skills training programs designed for both beginners and corporate teams. Our flexible approach allows us to tailor the training content, duration, and pricing based on your specific organizational needs and requirements. Perfect for enhancing communication, teamwork, leadership, and professional development skills.',
      duration: 'Custom',
      level: 'All Levels',
      price: 'Based on requirement',
      features: [
        'For Beginner & Corporates',
        'Customized training programs',
        'Tailored to organizational needs'
      ],
      instructor: 'Vasanthi S.E & Sunil Pandita',
      instructorBio: 'Vasanthi S.E: Senior Director – Pharmacovigilance & Medical Safety with nearly 24 years of experience. Sunil Pandita: Chief Strategic & Scientific Officer with nearly 20 years of experience in clinical research.',
    },
  };

  const numericCourseId = Number(id) || 1;
  const course = courses[numericCourseId] || courses[1];
  const coursePath = `/training/courses/${numericCourseId}`;
  // Get all trainers assigned to this course
  const assignedTrainers = trainers.filter((trainer) =>
    trainer.courses.includes(numericCourseId)
  );
  const courseStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description,
    provider: {
      '@type': 'Organization',
      name: 'Thranatech.com',
      url: 'https://www.thranatech.com',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: course.level,
      duration: course.duration,
      instructor: assignedTrainers.map((trainer) => ({
        '@type': 'Person',
        name: trainer.name,
        jobTitle: trainer.title,
      })),
    },
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone) => {
    // Allow various phone formats: +91 1234567890, 123-456-7890, (123) 456-7890, 1234567890, etc.
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    if (!phone) {
      return 'Phone number is required';
    }
    // Remove spaces, dashes, parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
    if (cleanedPhone.length < 10 || cleanedPhone.length > 15) {
      return 'Phone number must be between 10 and 15 digits';
    }
    if (!phoneRegex.test(phone)) {
      return 'Please enter a valid phone number';
    }
    return '';
  };

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (inquiryErrors[name]) {
      setInquiryErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Real-time validation
    if (name === 'email') {
      const emailError = validateEmail(value);
      setInquiryErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    } else if (name === 'phone') {
      const phoneError = validatePhone(value);
      setInquiryErrors((prev) => ({
        ...prev,
        phone: phoneError,
      }));
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {
      name: inquiryForm.name.trim() ? '' : 'Name is required',
      email: validateEmail(inquiryForm.email),
      phone: validatePhone(inquiryForm.phone),
      message: inquiryForm.message.trim() ? '' : 'Message is required',
      company: '',
    };

    setInquiryErrors(newErrors);

    // Check if there are any errors
    if (newErrors.name || newErrors.email || newErrors.phone || newErrors.message) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form before submitting.',
        severity: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get EmailJS configuration - supports both .env (development) and public config (production)
      // Priority: 1. Environment variables (.env) 2. Public config file (window.EMAILJS_CONFIG)
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || window.EMAILJS_CONFIG?.SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || window.EMAILJS_CONFIG?.TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || window.EMAILJS_CONFIG?.PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey || 
          serviceId === 'your_service_id_here' || 
          templateId === 'your_template_id_here' || 
          publicKey === 'your_public_key_here') {
        throw new Error('Email service is not configured. Please contact us directly at contact@thranatech.com');
      }

      // Prepare template parameters
      const templateParams = {
        from_name: inquiryForm.name,
        from_email: inquiryForm.email,
        phone: inquiryForm.phone || 'N/A',
        company: inquiryForm.company || 'N/A',
        message: inquiryForm.message || '',
        source: 'course_detail',
        type: 'course_inquiry',
        course: course.title,
        date: new Date().toLocaleString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        to_email: 'contact@thranatech.com',
      };

      // Send email using EmailJS with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout. Please check your internet connection and try again.')), 15000)
      );

      await Promise.race([
        emailjs.send(serviceId, templateId, templateParams, publicKey),
        timeoutPromise
      ]);
      
      setSnackbar({
        open: true,
        message: 'Thank you for your inquiry! We will contact you soon.',
        severity: 'success',
      });
      
      setInquiryForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
      });
      setInquiryDialogOpen(false);
    } catch (error) {
      console.error('EmailJS error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to submit inquiry. Please try again later.';
      
      if (error.text) {
        // EmailJS specific error
        if (error.text.includes('Invalid template ID') || error.text.includes('Template not found')) {
          errorMessage = 'Email service configuration error. Please contact us directly at contact@thranatech.com';
        } else if (error.text.includes('Invalid service ID')) {
          errorMessage = 'Email service configuration error. Please contact us directly at contact@thranatech.com';
        } else if (error.text.includes('Rate limit')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else {
          errorMessage = error.text;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Add fallback email option in error message
      const fallbackMessage = `${errorMessage} Alternatively, you can email us directly at contact@thranatech.com`;
      
      setSnackbar({
        open: true,
        message: fallbackMessage,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <SEO
        title={`${course.title} | Thrana Training`}
        description={course.fullDescription}
        path={coursePath}
        structuredData={courseStructuredData}
      />
      <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/training')}
          sx={{ mb: 3, color: '#3c005a' }}
        >
          Back to Training
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <StyledPaper elevation={3}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#3c005a' }}>
                {course.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip label={course.level} color="primary" />
                <Chip label={course.duration} />
                {/* <Chip label={course.price} color="secondary" /> */}
              </Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#3c005a', mt: 3 }}>
                Course Overview
              </Typography>
              <Typography variant="body1" paragraph>
                {course.fullDescription}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ color: '#3c005a', mt: 4 }}>
                What You'll Learn
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {course.features.map((feature, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <CheckCircleIcon sx={{ color: '#3c005a', mr: 1, mt: 0.5 }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Instructor
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  {course.instructor}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {course.instructorBio}
                </Typography>
              </Box>
            </StyledPaper>

            {/* Trainer bio section - hidden but kept for future use */}
            {false && assignedTrainers.length > 0 && (
              <StyledPaper elevation={3}>
                <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                  {assignedTrainers.length > 1 ? 'About the Trainers' : 'About the Trainer'}
                </Typography>
                {assignedTrainers.map((trainer, index) => (
                  <Box key={trainer.id} sx={{ mb: index < assignedTrainers.length - 1 ? 4 : 0 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="h6">{trainer.name}</Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          {trainer.title}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {trainer.bio}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#3c005a' }}>
                          Expertise Highlights
                        </Typography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {trainer.expertise.slice(0, 3).map((item) => (
                            <Grid item key={item}>
                              <Paper sx={{ px: 1.5, py: 0.5, bgcolor: '#f1e7ff' }}>{item}</Paper>
                            </Grid>
                          ))}
                        </Grid>
                        <Button
                          component={Link}
                          to="/trainers"
                          size="small"
                          sx={{ mt: 2, color: '#3c005a' }}
                        >
                          View trainer bio
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </StyledPaper>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                  Course Details
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {course.duration}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Level
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {course.level}
                  </Typography>
                </Box>
                {/* <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Price
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#3c005a' }}>
                    {course.price}
                  </Typography>
                </Box> */}
                <Box sx={{ mt: 4 }}>
                  <ButtonComponent
                    fullWidth
                    size="large"
                    onClick={() => setInquiryDialogOpen(true)}
                    sx={{ mb: 2 }}
                  >
                    Enroll Now
                  </ButtonComponent>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => setInquiryDialogOpen(true)}
                    sx={{ borderColor: '#3c005a', color: '#3c005a' }}
                  >
                    Request Information
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={inquiryDialogOpen} onClose={() => setInquiryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Course Inquiry</DialogTitle>
        <form onSubmit={handleInquirySubmit}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fill out the form below or email us directly at{' '}
              <a href="mailto:contact@thranatech.com" style={{ color: '#3c005a', textDecoration: 'none' }}>
                contact@thranatech.com
              </a>
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Full Name"
              name="name"
              fullWidth
              required
              value={inquiryForm.name}
              onChange={handleInquiryChange}
              disabled={isSubmitting}
              error={!!inquiryErrors.name}
              helperText={inquiryErrors.name}
            />
            <TextField
              margin="dense"
              label="Email"
              name="email"
              type="email"
              fullWidth
              required
              value={inquiryForm.email}
              onChange={handleInquiryChange}
              disabled={isSubmitting}
              error={!!inquiryErrors.email}
              helperText={inquiryErrors.email}
              inputProps={{
                pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$',
              }}
            />
            <TextField
              margin="dense"
              label="Phone"
              name="phone"
              type="tel"
              fullWidth
              required
              value={inquiryForm.phone}
              onChange={handleInquiryChange}
              disabled={isSubmitting}
              error={!!inquiryErrors.phone}
              helperText={inquiryErrors.phone || 'Format: +91 1234567890 or 123-456-7890'}
              inputProps={{
                pattern: '[\\+]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[0-9]{1,9}',
              }}
            />
            <TextField
              margin="dense"
              label="Company/Organization"
              name="company"
              fullWidth
              value={inquiryForm.company}
              onChange={handleInquiryChange}
              disabled={isSubmitting}
            />
            <TextField
              margin="dense"
              label="Message"
              name="message"
              multiline
              rows={4}
              fullWidth
              value={inquiryForm.message}
              onChange={handleInquiryChange}
              disabled={isSubmitting}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInquiryDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
      <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default CourseDetail;

